<script setup lang="ts">
const props = defineProps<{ facebook?: boolean, twitter?: boolean, linkedIn?: boolean, directLink?: boolean, twitterUser?: string, twitterHashtags?: string, title?: string, addedUrl?: string, reviewId?: string }>()

interface Network {
  name: string
  shareUrl: string
  args?: {
    title?: string
    user?: string
    hashtags?: string
    image?: string
  }
};

const fb: Network = {
  name: 'Facebook',
  shareUrl: 'https://www.facebook.com/sharer/sharer.php?u=[u]',
}

const tw: Network = {
  name: 'X',
  shareUrl: 'https://twitter.com/intent/tweet?url=[u]',
  args: {
    title: '&text=[t]',
    user: '&via=[uid]',
    hashtags: '&hashtags=[h]',
  },
}

const li: Network = {
  name: 'linkedin',
  shareUrl: 'https://www.linkedin.com/sharing/share-offsite/?url=[u]',
}

const networksIndex = {
  facebook: fb,
  twitter: tw,
  linkedin: li,
}

type NetworkKey = keyof typeof networksIndex

const route = useRoute()

const currentUrl = ref('')
function getPageUrl(): string {
  if (currentUrl.value) {
    return currentUrl.value
  }

  const url = new URL(useRequestURL().href)

  if (props.addedUrl) {
    const clean = props.addedUrl.startsWith('?')
      ? props.addedUrl.substring(1)
      : props.addedUrl

    const params = new URLSearchParams(clean)

    for (const [key, value] of params.entries()) {
      url.searchParams.set(key, value)
    }
  }

  return url.toString()
}

onMounted(() => {
  currentUrl.value = getPageUrl()
})

function share(platform: NetworkKey): string {
  const selectedNetwork = networksIndex[platform]
  const url = getUrl(selectedNetwork)
  return url
}

const shareTargetUrl = computed(() => {
  const pageUrl = getPageUrl()
  const routeReview = route.query.review
  let reviewId = props.reviewId ?? (Array.isArray(routeReview) ? routeReview[0] : routeReview)

  if (!reviewId) {
    const parsedUrl = new URL(pageUrl)
    reviewId = parsedUrl.searchParams.get('review') || undefined
  }

  if (!reviewId) {
    return pageUrl
  }

  const reviewUrl = new URL(pageUrl)
  if (reviewUrl.searchParams.get('type') === 'gbf') {
    reviewUrl.pathname = `/reviews/${encodeURIComponent(reviewId)}`
    reviewUrl.search = '?type=gbf'
    reviewUrl.hash = ''

    return reviewUrl.toString()
  }

  reviewUrl.pathname = `/reviews/${encodeURIComponent(reviewId)}`
  reviewUrl.search = ''
  reviewUrl.hash = ''

  return reviewUrl.toString()
})

function getUrl(network: Network): string {
  const selectedNetwork = {
    network: '',
    url: undefined,
    title: undefined,
    user: undefined,
    hashtags: undefined,
    image: undefined,
    ...network,
  }

  // Build full share raw url
  const shareUrl = selectedNetwork.shareUrl
  const argTitle = (selectedNetwork.args?.title && props.title) ? selectedNetwork.args?.title : ''
  const argUser = (selectedNetwork.args?.user && props.twitterUser) ? selectedNetwork.args?.user : ''
  const argHashtags = (selectedNetwork.args?.hashtags && props.twitterHashtags) ? selectedNetwork.args?.hashtags : ''

  let fullUrl = shareUrl + argTitle + argUser + argHashtags

  // Replace placeholders with actual values
  let user = props.twitterUser || ''
  if (user.startsWith('@')) {
    user = user.substring(1)
  }

  fullUrl = fullUrl
    .replace(/\[u\]/i, encodeURIComponent(shareTargetUrl.value))
    .replace(/\[t\]/i, encodeURIComponent(props.title || ''))
    .replace(/\[uid\]/i, encodeURIComponent(user || ''))
    .replace(/\[h\]/i, encodeURIComponent(props.twitterHashtags?.replace('@', '') || ''))

  return fullUrl
}

const { copy: copyLink, copied, isSupported } = useClipboard({ source: shareTargetUrl })
const { getIcon } = useSocials()
function onClickCopy() {
  copyLink()
}
</script>

<template>
  <div class="flex flex-wrap gap-2">
    <template v-if="facebook">
      <a
        :href="share('facebook')"
        target="_blank"
        class=" bg-[#4267B2] text-white flex w-auto items-center group duration-200 rounded-md shadow overflow-clip hover:bg-white hover:text-[#4267B2]"
      >
        <UIcon
          :name="getIcon('facebook')"
          class="px-3 inline-block ml-2 py-2 w-3 h-3 md:w-4 md:h-4 lg:w-5 lg:h-5 xl:w-6 xl:h-6  bg-white text-[#4267B2] group-hover:bg-[#4267B2] group-hover:text-white"
        />
        <div class="flex-shrink-0 inline-block px-2 py-1 md:px-3 lg:px-4">
          Share
          Facebook
        </div>
      </a>
    </template>
    <template v-if="twitter">
      <a
        :href="share('twitter')"
        target="_blank"
        class=" bg-[#1DA1F2] text-white flex w-auto items-center group duration-200 rounded-md shadow overflow-clip hover:bg-white hover:text-[#1DA1F2]"
      >
        <UIcon
          :name="getIcon('twitter')"
          class="px-3 inline-block ml-2 py-2 w-3 h-3 md:w-4 md:h-4 lg:w-5 lg:h-5 xl:w-6 xl:h-6  bg-white text-[#1DA1F2] group-hover:bg-[#1DA1F2] group-hover:text-white"
        />
        <div class="flex-shrink-0 inline-block px-2 py-1 md:px-3 lg:px-4">
          Share
          X
        </div>
      </a>
    </template>
    <template v-if="linkedIn">
      <a
        :href="share('linkedin')"
        target="_blank"
        class=" bg-[#0077B5] text-white flex w-auto items-center group duration-200 rounded-md shadow overflow-clip hover:bg-white hover:text-[#0077B5]"
      >
        <UIcon
          :name="getIcon('linkedin')"
          class="px-3 inline-block ml-2 py-2 w-3 h-3 md:w-4 md:h-4 lg:w-5 lg:h-5 xl:w-6 xl:h-6  bg-white text-[#0077B5] group-hover:bg-[#0077B5] group-hover:text-white"
        />
        <div class="flex-shrink-0 inline-block px-2 py-1 md:px-3 lg:px-4">
          Share
          LinkedIn
        </div>
      </a>
    </template>
    <template v-if="directLink && isSupported">
      <button
        class=" bg-white text-pink flex w-auto items-center group duration-200 rounded-md shadow overflow-clip hover:bg-lightblue hover:text-white"
        @click="onClickCopy()"
      >
        <UIcon
          name="ri:link"
          class="px-3 inline-block ml-2 py-2 w-3 h-3 md:w-4 md:h-4 lg:w-5 lg:h-5 xl:w-6 xl:h-6  bg-pink group-hover:bg-white"
        />
        <div class="flex-shrink-0 inline-block px-2 py-1 md:px-3 lg:px-4">
          {{ copied ? 'Copied!' : 'Copy Link' }}
        </div>
      </button>
    </template>
  </div>
</template>
